import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField, MenuItem, IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import useLoader from "../../../hooks/useLoader.js";
import useModal from "../../../hooks/useModal.js";
import { formatDateToYYYYMMDD, formatDisplayPeriod } from "../../../Helper/functionHelper.js";
import { getPayBasisList } from "../../../services/payBasis.js";
import { calculatePayroll, generatePayroll, PAYROLL_STATUS, PAYROLL_STATUS_LABELS } from "../../../services/payrollPeriodic.js";
import PayheadBreakdownView from "../shared/PayheadBreakdownView.jsx";

const PAY_BASIS_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" },
];

const DATE_HELP = {
  WEEKLY: "Pick any date inside the week (Mon-Sun) being paid.",
  BIWEEKLY: "Pick any date inside the 14-day fortnight (starting Monday) being paid.",
};

const employeeLabel = (emp) => (emp ? `${emp.employee_name} (${emp.employee_code})` : "");

const payableUnitsLabel = (payBasis) => (payBasis === "HOURLY" ? "Total Payable Hours" : "Total Payable Days");

const sumAmounts = (rows) => rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

const GeneratePayroll = ({ refresh }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();

  const [payBasis, setPayBasis] = useState("DAILY");
  // WEEKLY/BIWEEKLY: a single anchor date, snapped to that week/fortnight
  // server-side. DAILY/HOURLY: an explicit date range - one payslip
  // covering however many days/hours fall in it (a week, a month,
  // whatever you need), not forced into a single calendar day.
  const isRangeCapable = payBasis === "DAILY" || payBasis === "HOURLY";
  // Payroll can't be generated for a period that hasn't happened yet -
  // caps every date field below at today, both via the picker's own
  // maxDate and (never trusting that alone) the submit-time check in
  // validateDates().
  const today = new Date();
  const [date, setDate] = useState(new Date());
  const [rangeFrom, setRangeFrom] = useState(new Date());
  const [rangeTo, setRangeTo] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    setSelectedEmployee(null);
    setPreview(null);

    const fetchEmployees = async () => {
      try {
        const response = await getPayBasisList(1, { filters: { pay_basis: payBasis }, limit: 1000 });
        setEmployees(response?.data?.data?.data || []);
      } catch (error) {
        console.error("Error fetching employees for pay basis:", error);
        toast.error("Failed to load employees for this pay basis");
      }
    };
    fetchEmployees();
  }, [payBasis]);

  const buildPayload = () => ({
    employee_id: selectedEmployee?.employee_id,
    pay_basis: payBasis,
    ...(isRangeCapable
      ? { period_start: formatDateToYYYYMMDD(rangeFrom), period_end: formatDateToYYYYMMDD(rangeTo) }
      : { date: formatDateToYYYYMMDD(date) }),
    deductions: deductions
      .filter((d) => d.label && d.amount !== "")
      .map((d) => ({ label: d.label, amount: Number(d.amount) })),
    earnings: earnings
      .filter((e) => e.label && e.amount !== "")
      .map((e) => ({ label: e.label, amount: Number(e.amount) })),
  });

  const validateDates = () => {
    if (isRangeCapable && rangeTo < rangeFrom) {
      toast.error('"To" date must be on or after "From" date');
      return false;
    }
    // rangeFrom <= rangeTo always holds above, so checking just rangeTo
    // (or the single anchor date) is enough to rule out a future period
    // entirely - the DatePicker's own maxDate already stops this in the
    // UI, but that's never trusted alone, same as every other check here.
    if ((isRangeCapable ? rangeTo : date) > today) {
      toast.error("Cannot generate payroll for a future date");
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }
    if (!validateDates()) return;

    setIsBusy(true);
    showLoader();
    try {
      const response = await calculatePayroll(buildPayload());
      setPreview(response?.data?.data);
    } catch (error) {
      console.error("Error previewing payroll:", error);
      toast.error(error?.response?.data?.message || "Failed to calculate payroll");
      setPreview(null);
    } finally {
      setIsBusy(false);
      hideLoader();
    }
  };

  const handleGenerate = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }
    if (!validateDates()) return;

    setIsBusy(true);
    showLoader();
    try {
      const response = await generatePayroll(buildPayload());
      toast.success(response?.data?.message || "Payroll generated successfully!");
      refresh?.();
      hideModal?.();
    } catch (error) {
      console.error("Error generating payroll:", error);
      toast.error(error?.response?.data?.message || "Failed to generate payroll");
    } finally {
      setIsBusy(false);
      hideLoader();
    }
  };

  const addDeductionRow = () => setDeductions((prev) => [...prev, { label: "", amount: "" }]);
  const updateDeductionRow = (index, key, value) =>
    setDeductions((prev) => prev.map((d, i) => (i === index ? { ...d, [key]: value } : d)));
  const removeDeductionRow = (index) => setDeductions((prev) => prev.filter((_, i) => i !== index));

  const addEarningRow = () => setEarnings((prev) => [...prev, { label: "", amount: "" }]);
  const updateEarningRow = (index, key, value) =>
    setEarnings((prev) => prev.map((e, i) => (i === index ? { ...e, [key]: value } : e)));
  const removeEarningRow = (index) => setEarnings((prev) => prev.filter((_, i) => i !== index));

  // Worked units, wage rate lookup, and payhead resolution genuinely need
  // a server round-trip (Preview button) - but Manual Earnings/Deductions
  // are just flat add/subtract on top of whatever the server already
  // computed, so once a preview exists, editing those rows recomputes
  // Total Income/Deduction/Net Pay live, without waiting for another
  // Preview click. "Base" subtracts out whatever manual amounts were
  // actually SENT for that server response (preview.manual_earnings/
  // manual_deductions), then adds back in the CURRENT row values.
  const liveTotalIncome = preview ? preview.total_income - sumAmounts(preview.manual_earnings || []) + sumAmounts(earnings) : null;
  const liveDeduction = preview ? preview.deduction - sumAmounts(preview.manual_deductions || []) + sumAmounts(deductions) : null;
  const liveNetPay = preview ? liveTotalIncome - liveDeduction : null;

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Pay Basis
          </label>
          <TextField select size="small" fullWidth value={payBasis} onChange={(e) => setPayBasis(e.target.value)} disabled={isBusy}>
            {PAY_BASIS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {isRangeCapable ? (
          <>
            <div className="col-md-2 mb-3">
              <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
                From
              </label>
              <DatePicker
                format="dd-MMM-yyyy"
                value={rangeFrom}
                maxDate={rangeTo < today ? rangeTo : today}
                onChange={(value) => {
                  setRangeFrom(value);
                  setPreview(null);
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
                To
              </label>
              <DatePicker
                format="dd-MMM-yyyy"
                value={rangeTo}
                minDate={rangeFrom}
                maxDate={today}
                onChange={(value) => {
                  setRangeTo(value);
                  setPreview(null);
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <div className="form-text">One payslip covering every day in this range.</div>
            </div>
          </>
        ) : (
          <div className="col-md-4 mb-3">
            <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
              Date
            </label>
            <DatePicker
              format="dd-MMM-yyyy"
              value={date}
              maxDate={today}
              onChange={(value) => {
                setDate(value);
                setPreview(null);
              }}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <div className="form-text">{DATE_HELP[payBasis]}</div>
          </div>
        )}

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Employee <span className="text-danger">*</span>
          </label>
          <Autocomplete
            size="small"
            options={employees}
            getOptionLabel={employeeLabel}
            isOptionEqualToValue={(opt, val) => opt.employee_id === val.employee_id}
            value={selectedEmployee}
            onChange={(e, value) => {
              setSelectedEmployee(value);
              setPreview(null);
            }}
            disabled={isBusy}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select employee" />}
          />
          {employees.length === 0 && (
            <div className="form-text text-danger">No employees are assigned this pay basis yet.</div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0" style={{ fontWeight: 600, fontSize: "12px" }}>
            Manual Earnings (optional)
          </label>
          <button type="button" className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={addEarningRow}>
            <AddCircleOutline sx={{ fontSize: 16 }} /> Add
          </button>
        </div>

        {earnings.map((e, index) => (
          <div className="d-flex gap-2 align-items-center mb-2" key={index}>
            <TextField
              size="small"
              placeholder="e.g. One-off bonus"
              value={e.label}
              onChange={(ev) => updateEarningRow(index, "label", ev.target.value)}
              style={{ flex: 2 }}
            />
            <TextField
              size="small"
              type="number"
              placeholder="Amount"
              value={e.amount}
              onChange={(ev) => updateEarningRow(index, "amount", ev.target.value)}
              style={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => removeEarningRow(index)} title="Remove">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </div>
        ))}

        <div className="form-text">Added straight to Total Income - a one-off bonus, reimbursement, or arrear payment.</div>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0" style={{ fontWeight: 600, fontSize: "12px" }}>
            Manual Deductions (optional)
          </label>
          <button type="button" className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={addDeductionRow}>
            <AddCircleOutline sx={{ fontSize: 16 }} /> Add
          </button>
        </div>

        {deductions.map((d, index) => (
          <div className="d-flex gap-2 align-items-center mb-2" key={index}>
            <TextField
              size="small"
              placeholder="e.g. Advance recovery"
              value={d.label}
              onChange={(e) => updateDeductionRow(index, "label", e.target.value)}
              style={{ flex: 2 }}
            />
            <TextField
              size="small"
              type="number"
              placeholder="Amount"
              value={d.amount}
              onChange={(e) => updateDeductionRow(index, "amount", e.target.value)}
              style={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => removeDeductionRow(index)} title="Remove">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </div>
        ))}

        <div className="form-text">
          Statutory deductions (PF/ESI/PT/TDS) aren't computed automatically for this pay basis yet - add any
          deduction manually until that policy is finalized.
        </div>
      </div>

      {preview?.conflicts?.length > 0 && (
        <div className="alert alert-warning mb-3" style={{ fontSize: "13px" }}>
          <strong>Overlaps existing payroll:</strong>
          <ul className="mb-0 mt-1">
            {preview.conflicts.map((c) => (
              <li key={c.id}>
                {formatDisplayPeriod(c.period_start, c.period_end)} (status: {PAYROLL_STATUS_LABELS[Number(c.status)] || c.status})
              </li>
            ))}
          </ul> 
          Adjust the date range so it doesn't overlap, or resolve that record first - Generate is disabled until then.
        </div>
      )}

      {preview?.existing_record &&
        (Number(preview.existing_record.status) === PAYROLL_STATUS.REJECTED ? (
          <div className="alert alert-info mb-3" style={{ fontSize: "13px" }}>
            This exact period was previously <strong>Rejected</strong>. Generating again will create a fresh record
            for it, with these numbers.
          </div>
        ) : (
          <div className="alert alert-danger mb-3" style={{ fontSize: "13px" }}>
            A payroll record for this exact period already exists (Status:{" "}
            {PAYROLL_STATUS_LABELS[Number(preview.existing_record.status)] || preview.existing_record.status}, Net
            Pay: ₹{Number(preview.existing_record.net_pay).toFixed(2)}). Reject it first before generating again -
            Generate is disabled until then.
          </div>
        ))}

      {preview && (
        <div className="p-3 mb-3" style={{ backgroundColor: "#f9f9f9", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
          <div className="fw-semibold mb-2">Preview - {formatDisplayPeriod(preview.period?.start, preview.period?.end)}</div>
          <div className="row" style={{ fontSize: "13px" }}>
            <div className="col-6 col-md-4 mb-1">Days Present: {preview.worked_units?.daysPresent}</div>
            <div className="col-6 col-md-4 mb-1">Hours Worked: {preview.worked_units?.hoursWorked}</div>
            <div className="col-6 col-md-4 mb-1">Week-off Days: {preview.worked_units?.weekOffDays}</div>
            <div className="col-6 col-md-4 mb-1">Holiday Days: {preview.worked_units?.holidayDays}</div>
            <div className="col-6 col-md-4 mb-1">Leave Days: {preview.worked_units?.leaveDays}</div>
            <div className="col-6 col-md-4 mb-1">LWP Days: {preview.worked_units?.lwpDays}</div>
            <div className="col-6 col-md-4 mb-1">Pay Basis: {preview.pay_basis}</div>
            <div className="col-6 col-md-4 mb-1">
              {payableUnitsLabel(preview.pay_basis)}: {preview.units_worked}
            </div>
            <div className="col-6 col-md-4 mb-1">Rate Applied: ₹{Number(preview.rate_applied).toFixed(2)}</div>
          </div>

          {preview.payheads?.length > 0 && (
            <>
              <hr />
              <PayheadBreakdownView payheads={preview.payheads} />
            </>
          )}
          <hr />
          <div className="row" style={{ fontSize: "14px" }}>
            <div className="col-4 fw-semibold">Total Income: ₹{liveTotalIncome.toFixed(2)}</div>
            <div className="col-4 fw-semibold">Deduction: ₹{liveDeduction.toFixed(2)}</div>
            <div className="col-4 fw-semibold text-success">Net Pay: ₹{liveNetPay.toFixed(2)}</div>
          </div>
          {(sumAmounts(earnings) !== sumAmounts(preview.manual_earnings || []) ||
            sumAmounts(deductions) !== sumAmounts(preview.manual_deductions || [])) && (
            <div className="form-text mt-1">Updated live from your Manual Earnings/Deductions above - click Preview again to also refresh worked units.</div>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
        <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isBusy}>
          Cancel
        </button>
        <button type="button" className="btn btn-outline-primary btn-sm" disabled={isBusy} onClick={handlePreview}>
          Preview
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={
            isBusy ||
            preview?.conflicts?.length > 0 ||
            (preview?.existing_record && Number(preview.existing_record.status) !== PAYROLL_STATUS.REJECTED)
          }
          onClick={handleGenerate}
        >
          Generate
        </button>
      </div>
    </div>
  );
};

export default GeneratePayroll;
